import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { SectionCard } from "../components/ui";
import { fmtDate } from "../utils";
import { useGlobalTodo, useGlobalTodoFilters, useProjectTodo } from "./hooks";
import { RESPONSIBLE_ROLE_PL } from "./labels";
import { quickTargetOf, QuickTargetModal, type QuickTarget } from "./QuickTargetModal";
import {
  CourtPill, DeadlineCell, ItemCell, Segmented, StatStrip, TABLE_CLS, Td, Th, TRow,
  useTableSort, type SortAccessors, type Tone,
} from "./tableUi";
import type { TodoItem } from "./types";

// „Etap" to w rzeczywistości kamień milowy z tablicy kamieni — nazywamy go tak samo.
const KIND_PL: Record<string, string> = {
  milestone: "Kamień", proceeding: "Sprawa urzędowa", parcel: "Działka", location: "Działka",
};
const COURT_PL: Record<string, string> = {
  ours: "nasz ruch",
  office: "czekamy na odpowiedź",
  negotiator: "negocjator w terenie",
  blocked: "zablokowane",
};

/** Multiselect z wyszukiwarką i „zaznacz/odznacz wyszukane" (projekty, prowadzący). */
function FacetMultiSelect({ label, options, selected, onChange }: {
  label: string; options: string[]; selected: Set<string>; onChange: (s: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const visible = options.filter((o) => o.toLowerCase().includes(q.toLowerCase()));
  const toggle = (o: string) => {
    const next = new Set(selected);
    if (next.has(o)) next.delete(o); else next.add(o);
    onChange(next);
  };
  return (
    <div className="relative inline-block">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={`px-3 py-1.5 border font-mono text-[11px] uppercase tracking-wide inline-flex items-center gap-1 transition-colors ${selected.size > 0
          ? "border-[var(--color-primary)] text-[var(--color-primary)]"
          : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]"}`}>
        {label}{selected.size > 0 ? `: ${selected.size}` : ": wszystkie"} <span className="text-[9px]">▾</span>
      </button>
      {open && (
        <>
          {/* klik poza panelem zamyka */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 w-80 max-h-96 overflow-auto border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-lg p-2 space-y-1.5">
            <input autoFocus className="input-base w-full text-xs" placeholder="Szukaj…"
              value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="flex flex-wrap gap-2 text-[10px] font-mono text-[var(--color-primary)]">
              <button type="button" onClick={() => onChange(new Set([...selected, ...visible]))}>
                zaznacz wyszukane ({visible.length})
              </button>
              <button type="button" onClick={() => { const n = new Set(selected); visible.forEach((v) => n.delete(v)); onChange(n); }}>
                odznacz wyszukane
              </button>
              <button type="button" onClick={() => onChange(new Set())}>wyczyść</button>
            </div>
            {visible.map((o) => (
              <label key={o} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[var(--color-bg-elevated)] px-1 py-0.5">
                <input type="checkbox" checked={selected.has(o)} onChange={() => toggle(o)} />
                <span className="truncate" title={o}>{o}</span>
              </label>
            ))}
            {visible.length === 0 && <div className="text-xs text-[var(--color-text-muted)] px-1">Brak wyników.</div>}
          </div>
        </>
      )}
    </div>
  );
}

/** Termin + pilność w jednej komórce (data, pod nią stan) — zamiast osobnej
 * kolumny z dużymi chipami, która rozpychała tabelę. */
function UrgencyCell({ i }: { i: TodoItem }) {
  const overKpa = i.breach_reason === "kpa" ? (i.kpa_days_over ?? 0) : 0;
  const date = i.due_date ? fmtDate(i.due_date) : null;
  if (i.sla_breached) {
    const over = overKpa > 0 ? overKpa : i.days_left != null ? Math.max(1, -i.days_left) : null;
    return (
      <DeadlineCell
        date={date}
        tone="late"
        state={`po terminie${over != null ? ` o ${over} dn.` : ""}${i.breach_reason === "kpa" ? " (KPA)" : ""}`}
      />
    );
  }
  if (i.days_left == null) return <DeadlineCell date={date} tone="ok" state="bez terminu" />;
  return <DeadlineCell date={date} tone={i.warn ? "warn" : "ok"} state={`zostało ${i.days_left} dn.`} />;
}

/** Kroki koncepcyjne (opracowanie + uzgodnienia koncepcji) — koordynator musi
 * najpierw rozstrzygnąć, kto je realizuje, więc oznaczamy je wykrzyknikiem. */
const CONCEPT_HINT =
  "Uwaga dla koordynatora: przeanalizuj, czy ten krok jest wymagany do wykonania przez nas, czy przez projektanta.";
function isConceptStep(i: TodoItem): boolean {
  if (i.kind !== "milestone") return false;
  const key = (i.ref?.milestone_key ?? "").toLowerCase();
  return key.includes("koncepcj") || i.label.toLowerCase().includes("koncepcj");
}

function statusOf(i: TodoItem): "late" | "warn" | "ok" {
  return i.sla_breached ? "late" : i.warn ? "warn" : "ok";
}

function toneOf(i: TodoItem): Tone {
  return i.sla_breached ? "late" : i.warn ? "warn" : i.stalled ? "idle" : "ok";
}

/** Wspólna lista TODO: nieblokowane pozycje od najpilniejszej (sortuje backend).
 * `items` z /todo/ (projekt) albo /advisory-todo/ (globalnie, z kolumną projektu). */
function TodoTable({ items, global, showPeople = global, connectionId }: {
  items: TodoItem[]; global: boolean; showPeople?: boolean;
  /** Projekt ZE listy projektowej — globalna ma id przy każdej pozycji. */
  connectionId?: string;
}) {
  const [court, setCourt] = useState("");     // czyj ruch
  const [kind, setKind] = useState("");       // typ pozycji
  const [status, setStatus] = useState("");   // late/warn/ok
  const [q, setQ] = useState("");

  const filtered = items.filter((i) =>
    (!court || i.whose_court === court)
    && (!kind || i.kind === kind)
    && (!status || statusOf(i) === status)
    && (!q || `${i.label} ${i.phase_label ?? ""} ${i.project_name ?? ""}`.toLowerCase().includes(q.toLowerCase())),
  );

  const late = items.filter((i) => i.sla_breached).length;
  const ourMove = items.filter((i) => i.whose_court === "ours").length;

  const warnCount = items.filter((i) => i.warn && !i.sla_breached).length;

  const accessors: SortAccessors<TodoItem> = {
    project: (i) => i.project_name ?? "",
    designer: (i) => i.designer ?? "",
    item: (i) => i.label,
    phase: (i) => i.phase_label ?? "",
    court: (i) => COURT_PL[i.whose_court] ?? i.whose_court,
    due: (i) => i.due_date ?? null,
    role: (i) => RESPONSIBLE_ROLE_PL[i.responsible_role] ?? i.responsible_role,
  };
  const { sort, toggle, sorted } = useTableSort(filtered, accessors);
  const [target, setTarget] = useState<QuickTarget | null>(null);

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="text-sm">✓ Nic do zrobienia — brak otwartych, nieblokowanych pozycji.</div>
      ) : (
        <StatStrip
          stats={[
            { label: "Pozycji łącznie", value: items.length },
            { label: "Nasz ruch", value: ourMove, tone: ourMove > 0 ? "warn" : "none" },
            { label: "Po terminie", value: late, tone: late > 0 ? "late" : "ok" },
            { label: "Blisko terminu", value: warnCount, tone: warnCount > 0 ? "warn" : "none" },
          ]}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Segmented value={court} onChange={setCourt} title="Czyj ruch"
          options={[["", "Wszystko"], ["ours", "Nasz ruch"], ["office", "Czekamy na odpowiedź"]]} />
        <Segmented value={kind} onChange={setKind} title="Typ pozycji"
          options={[["", "Wszystko"], ["milestone", "Kamienie"], ["proceeding", "Sprawy"], ["parcel", "Działki"]]} />
        <Segmented value={status} onChange={setStatus} title="Termin"
          options={[["", "Wszystko"], ["late", "Po terminie"], ["warn", "Blisko"], ["ok", "W terminie"]]} />
        <input className="input-base w-44 text-xs" placeholder="Szukaj…" value={q} onChange={(e) => setQ(e.target.value)} />
        {filtered.length !== items.length && (
          <span className="text-[10px] font-mono text-[var(--color-text-muted)]">{filtered.length} z {items.length}</span>
        )}
      </div>

      <div className="border-t border-[var(--color-border)]">
        <table className={TABLE_CLS}>
          <colgroup>
            {global && <col style={{ width: "20%" }} />}
            {showPeople && <col style={{ width: "9%" }} />}
            <col style={{ width: "18%" }} />
            <col />
            <col style={{ width: "13%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "11%" }} />
          </colgroup>
          <thead>
            <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
              {global && <Th sortKey="project" sort={sort} onSort={toggle}>Projekt</Th>}
              {showPeople && <Th sortKey="designer" sort={sort} onSort={toggle}>Prowadzi</Th>}
              <Th sortKey="item" sort={sort} onSort={toggle}>Pozycja</Th>
              <Th sortKey="phase" sort={sort} onSort={toggle}
                info="Bieżąca faza kroku — konkretna czynność, nie kod procesu.">
                Co zrobić
              </Th>
              <Th sortKey="court" sort={sort} onSort={toggle}>Czyj ruch</Th>
              <Th sortKey="role" sort={sort} onSort={toggle}>Kto</Th>
              <Th sortKey="due" sort={sort} onSort={toggle}
                info="Data, do której trzeba domknąć krok, i ile czasu zostało (albo o ile jest przekroczony). Domyślnie lista idzie od najpilniejszej pozycji.">
                Termin
              </Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((i, idx) => {
              const t = quickTargetOf(i, i.connection_id ?? connectionId);
              return (
              <TRow key={idx} tone={toneOf(i)}>
                {global && (
                  <Td>
                    <Link
                      className="hover:text-[var(--color-primary)] break-words"
                      title={i.project_name ?? undefined}
                      to={`/power-projects/${i.project_id}?tab=todo`}
                    >
                      {i.project_name}
                    </Link>
                  </Td>
                )}
                {showPeople && (
                  <Td className="font-mono text-xs text-[var(--color-text-muted)] break-words">{i.designer ?? "—"}</Td>
                )}
                <Td>
                  <div className="flex items-center gap-1.5">
                    <ItemCell
                      kind={KIND_PL[i.kind] ?? i.kind}
                      label={i.label}
                      onClick={t ? () => setTarget(t) : undefined}
                    />
                    {isConceptStep(i) && (
                      <span
                        title={CONCEPT_HINT}
                        aria-label={CONCEPT_HINT}
                        className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center border border-amber-500 font-mono text-[10px] font-bold text-amber-600"
                      >
                        !
                      </span>
                    )}
                  </div>
                </Td>
                <Td className="text-[var(--color-text-secondary)]">
                  <div className="leading-tight">
                    <div>{i.phase_label || "—"}</div>
                    {i.stalled && (
                      <div className="font-mono text-[10px] text-[var(--color-text-muted)] mt-0.5">
                        bez ruchu {i.days_idle} dn.
                      </div>
                    )}
                  </div>
                </Td>
                <Td><CourtPill court={i.whose_court} label={COURT_PL[i.whose_court] ?? i.whose_court} /></Td>
                <Td className="font-mono text-xs text-[var(--color-text-muted)]">
                  {RESPONSIBLE_ROLE_PL[i.responsible_role] ?? i.responsible_role}
                </Td>
                <Td><UrgencyCell i={i} /></Td>
              </TRow>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && items.length > 0 && (
          <div className="text-sm text-[var(--color-text-muted)] p-3">Brak pozycji dla wybranych filtrów.</div>
        )}
      </div>

      {target && <QuickTargetModal target={target} onClose={() => setTarget(null)} />}
    </div>
  );
}

/** TODO projektu — zakładka dla koordynatora/projektanta. */
export function ProjectTodoTab({ connectionId }: { connectionId: string }) {
  const { data, isLoading } = useProjectTodo(connectionId);
  return (
    <div className="mt-4">
      <SectionCard title="TODO — co teraz zrobić (od najpilniejszego)">
        {isLoading
          ? <div className="text-sm text-[var(--color-text-muted)]">Ładowanie…</div>
          : <TodoTable items={data?.items ?? []} global={false} connectionId={connectionId} />}
        <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2">
          Pokazujemy tylko pozycje NIEBLOKOWANE (w toku, złożone albo gotowe do startu — poprzednicy domknięci).
          Kroki czekające na poprzedników widać na grafie procesu.
        </p>
      </SectionCard>
    </div>
  );
}

const SEARCH_BTN =
  "inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-4 py-1.5 bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-50";

/** TODO „łącznie" — wszystkie projekty w jednej tabeli. Backend zwraca `scope`:
 *   "all"  → dyr. techniczny / CEO: wszystkie projekty ZE, filtr po osobie ("dla danego człowieka"),
 *   "self" → zwykły prowadzący: tylko własne projekty, bez filtra osób (jest tylko jedna).
 *
 * Liczenie pozycji jest drogie, więc najpierw pobieramy TYLKO opcje filtrów
 * (lekki `?filters=1` — kompletna lista projektów i prowadzących, niezależna od
 * danych), a pozycje dopiero po kliknięciu „Szukaj" — zawężone na backendzie.
 *
 * `defaultMine` (strona „Moje TODO") wyszukuje od razu po zalogowanej osobie,
 * żeby nie trzeba było za każdym wejściem wybierać samego siebie. Nadzór zostaje
 * bez auto-startu — tam chodzi o cały portfel, a to najdroższe zapytanie. */
export function GlobalTodoTab({ defaultMine = false }: { defaultMine?: boolean } = {}) {
  const { data: meta, isLoading: metaLoading } = useGlobalTodoFilters();
  const myId = useAuthStore((s) => s.user?.id);
  const scope = meta?.scope ?? "all";
  const [projects, setProjects] = useState<Set<string>>(new Set()); // etykiety (nazwy projektów)
  const [people, setPeople] = useState<Set<string>>(new Set());     // etykiety (imię i nazwisko)
  const [applied, setApplied] = useState<{ projects: string[]; designers: string[] } | null>(null);

  // Etykieta → id(y); nazwy mogą się teoretycznie powtórzyć, więc lista.
  const projectIds = useMemo(() => {
    const m = new Map<string, string[]>();
    meta?.projects.forEach((p) => m.set(p.name, [...(m.get(p.name) ?? []), p.id]));
    return m;
  }, [meta]);
  const designerIds = useMemo(() => {
    const m = new Map<string, string[]>();
    meta?.designers.forEach((d) => m.set(d.name, [...(m.get(d.name) ?? []), d.id]));
    return m;
  }, [meta]);

  const search = () => setApplied({
    projects: [...projects].flatMap((n) => projectIds.get(n) ?? []),
    designers: [...people].flatMap((n) => designerIds.get(n) ?? []),
  });

  // Auto-start „Moje TODO": zwykły prowadzący widzi tylko swoje projekty, więc
  // wystarczy puste zapytanie; dyrektor / CEO dostaje zasięg „all", więc
  // zawężamy po jego własnym id (dokładnie, nie po etykiecie — imiona bywają
  // niejednoznaczne). Kto nie prowadzi żadnego projektu, wybiera ręcznie.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!defaultMine || autoStarted.current || !meta) return;
    autoStarted.current = true;
    if (meta.scope !== "all") {
      setApplied({ projects: [], designers: [] });
      return;
    }
    const mine = meta.designers.find((d) => d.id === myId);
    if (!mine) return;
    setPeople(new Set([mine.name]));
    setApplied({ projects: [], designers: [mine.id] });
  }, [defaultMine, meta, myId]);

  const { data, isFetching } = useGlobalTodo(applied ?? undefined, applied != null);

  // Zasięg „all" zawężony do samego siebie (auto-start albo ręczny wybór) to nadal
  // „moje TODO" — tytuł nie może obiecywać całego portfela.
  const mineOnly = applied != null && applied.designers.length === 1 && applied.designers[0] === myId;
  const title = scope === "self" || mineOnly
    ? "Moje TODO — wszystkie moje projekty łącznie (od najpilniejszego)"
    : "TODO globalne — wszystkie projekty (od najpilniejszego)";
  return (
    <div className="mt-4">
      <SectionCard title={title}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <FacetMultiSelect label="Projekty" options={[...projectIds.keys()]}
            selected={projects} onChange={setProjects} />
          {scope === "all" && (
            <FacetMultiSelect label="Prowadzący" options={[...designerIds.keys()]}
              selected={people} onChange={setPeople} />
          )}
          <button type="button" className={SEARCH_BTN} onClick={search}
            disabled={metaLoading || isFetching}>
            {isFetching ? "Szukam…" : "Szukaj"}
          </button>
        </div>
        {applied == null ? (
          <div className="text-sm text-[var(--color-text-muted)]">
            {metaLoading
              ? "Ładowanie filtrów…"
              : "Zawęź projekty lub prowadzących (albo zostaw „wszystkie”) i kliknij „Szukaj”."}
          </div>
        ) : isFetching ? (
          <div className="text-sm text-[var(--color-text-muted)]">Ładowanie pozycji…</div>
        ) : (
          <TodoTable items={data?.items ?? []} global showPeople={scope === "all"} />
        )}
      </SectionCard>
    </div>
  );
}
